{
  "blocks": [
    {
      "id": "supplement-vitamin-a",
      "kind": "supplement",
      "tags": ["supplements", "his", "optimization", "pre-cycle", "sperm"],
      "title": "Vitamin A",
      "icon": "💊",
      "body": {
        "name": "Vitamin A",
        "dosageRange": "Usually covered by a multivitamin; AlphaSperm provides 1,500mcg RAE per daily serving",
        "dailyTiming": "Take with food, ideally with some fat. Avoid stacking high-dose vitamin A products.",
        "timing": "Useful as part of a daily preconception stack",
        "bioavailability": {
          "form": "AlphaSperm uses beta carotene plus retinyl palmitate, which combines a provitamin A carotenoid with preformed vitamin A.",
          "absorption": "Vitamin A is fat-soluble, so absorption is better with a meal that contains fat.",
          "metabolism": "Vitamin A supports cell differentiation and antioxidant defenses, but excess preformed vitamin A can be harmful."
        },
        "evidenceStrength": "moderate",
        "forWhom": "him",
        "foods": ["Eggs", "Milk", "Spinach"],
        "expectations": {
          "canExpect": [
            "Supports antioxidant defense and normal cell differentiation during sperm development",
            "Contributes to immune and reproductive tissue health",
            "Works as part of a multinutrient formula rather than as a standalone fertility treatment"
          ],
          "shouldNotExpect": [
            "More is not better; avoid stacking high-dose vitamin A or retinol products",
            "Do not treat it as a cure for abnormal semen analysis results",
            "Use extra caution with isotretinoin or liver disease history"
          ]
        }
      },
      "sources": [
        {
          "title": "Vitamin A Fact Sheet for Health Professionals",
          "journal": "NIH Office of Dietary Supplements",
          "year": 2025,
          "url": "https://ods.od.nih.gov/factsheets/VitaminA-HealthProfessional/",
          "evidenceLevel": "review"
        }
      ]
    },
    {
      "id": "supplement-vitamin-k",
      "kind": "supplement",
      "tags": ["supplements", "his", "optimization", "pre-cycle", "sperm"],
      "title": "Vitamin K",
      "icon": "💊",
      "body": {
        "name": "Vitamin K",
        "dosageRange": "Usually met through diet or a multivitamin; AlphaSperm provides 100mcg per daily serving",
        "dailyTiming": "Take consistently with food. If you take warfarin, do not change vitamin K intake without clinician guidance.",
        "timing": "Useful as part of a daily preconception stack",
        "bioavailability": {
          "form": "AlphaSperm uses phylloquinone, also called vitamin K1.",
          "absorption": "Vitamin K is fat-soluble and absorbs better with meals containing fat.",
          "metabolism": "It is best known for clotting-factor biology; animal data also connect vitamin K status with testicular function."
        },
        "evidenceStrength": "emerging",
        "forWhom": "him",
        "foods": ["Leafy greens", "Broccoli", "Spinach"],
        "expectations": {
          "canExpect": [
            "Supports normal vitamin K intake inside the broader male prenatal formula",
            "May help cover a diet gap for people with low leafy-green intake",
            "Adds fat-soluble nutrient support without being a primary sperm supplement"
          ],
          "shouldNotExpect": [
            "Direct proven improvement in sperm count, motility, or pregnancy outcomes",
            "Safe unsupervised use if you take warfarin or other anticoagulation",
            "A reason to add separate high-dose vitamin K on top of AlphaSperm"
          ]
        }
      },
      "sources": [
        {
          "title": "Vitamin K Fact Sheet for Health Professionals",
          "journal": "NIH Office of Dietary Supplements",
          "year": 2025,
          "url": "https://ods.od.nih.gov/factsheets/VitaminK-HealthProfessional/",
          "evidenceLevel": "review"
        }
      ]
    },
    {
      "id": "supplement-thiamin-b1",
      "kind": "supplement",
      "tags": ["supplements", "his", "optimization", "pre-cycle", "sperm"],
      "title": "Thiamin (B1)",
      "icon": "💊",
      "body": {
        "name": "Thiamin / Vitamin B1",
        "dosageRange": "AlphaSperm provides 4mg per daily serving",
        "dailyTiming": "Take with the rest of the formula. Morning or afternoon dosing is easiest for most B vitamins.",
        "timing": "Useful as part of a daily preconception stack",
        "bioavailability": {
          "form": "AlphaSperm uses thiamin hydrochloride.",
          "absorption": "Thiamin is water-soluble, so consistent daily intake matters more than occasional large doses.",
          "metabolism": "B1 helps convert food into cellular energy, which is relevant to high-energy tissues and sperm metabolism."
        },
        "evidenceStrength": "emerging",
        "forWhom": "him",
        "foods": ["Fortified grains", "Beans", "Pork"],
        "expectations": {
          "canExpect": [
            "Supports general energy metabolism in a male prenatal formula",
            "Helps cover B-vitamin gaps that are common with restricted diets",
            "Pairs with the rest of the B-complex nutrients used in AlphaSperm"
          ],
          "shouldNotExpect": [
            "A standalone fertility effect from thiamin alone",
            "Immediate changes in semen parameters",
            "A replacement for a nutrition review if diet quality is poor"
          ]
        }
      },
      "sources": [
        {
          "title": "Thiamin Fact Sheet for Health Professionals",
          "journal": "NIH Office of Dietary Supplements",
          "year": 2025,
          "url": "https://ods.od.nih.gov/factsheets/Thiamin-HealthProfessional/",
          "evidenceLevel": "review"
        }
      ]
    },
    {
      "id": "supplement-riboflavin-b2",
      "kind": "supplement",
      "tags": ["supplements", "his", "optimization", "pre-cycle", "sperm"],
      "title": "Riboflavin (B2)",
      "icon": "💊",
      "body": {
        "name": "Riboflavin / Vitamin B2",
        "dosageRange": "AlphaSperm provides 5mg per daily serving",
        "dailyTiming": "Take with the rest of the formula. Bright yellow urine can happen and is usually harmless.",
        "timing": "Useful as part of a daily preconception stack",
        "bioavailability": {
          "form": "AlphaSperm uses riboflavin-5-phosphate.",
          "absorption": "Riboflavin is water-soluble and works best as part of steady daily intake.",
          "metabolism": "B2 is needed for flavin coenzymes used in mitochondrial energy production and antioxidant recycling."
        },
        "evidenceStrength": "emerging",
        "forWhom": "him",
        "foods": ["Eggs", "Milk", "Almonds"],
        "expectations": {
          "canExpect": [
            "Supports mitochondrial and antioxidant enzyme systems",
            "Complements other B vitamins in sperm-energy pathways",
            "Helps fill a basic micronutrient gap rather than acting like a drug"
          ],
          "shouldNotExpect": [
            "Direct proven improvement in fertility outcomes from riboflavin alone",
            "Meaningful benefit if overall B-vitamin intake is already adequate",
            "A substitute for testing or treatment of male factor infertility"
          ]
        }
      },
      "sources": [
        {
          "title": "Riboflavin Fact Sheet for Health Professionals",
          "journal": "NIH Office of Dietary Supplements",
          "year": 2025,
          "url": "https://ods.od.nih.gov/factsheets/Riboflavin-HealthProfessional/",
          "evidenceLevel": "review"
        }
      ]
    },
    {
      "id": "supplement-niacin-b3",
      "kind": "supplement",
      "tags": ["supplements", "his", "optimization", "pre-cycle", "sperm"],
      "title": "Niacin (B3)",
      "icon": "💊",
      "body": {
        "name": "Niacin / Vitamin B3",
        "dosageRange": "AlphaSperm provides 30mg NE per daily serving",
        "dailyTiming": "Take with food if it causes flushing or stomach upset. AlphaSperm uses niacinamide, which is less flushing than nicotinic acid.",
        "timing": "Useful as part of a daily preconception stack",
        "bioavailability": {
          "form": "AlphaSperm uses niacinamide.",
          "absorption": "Niacin forms are water-soluble; daily consistency is the main practical point.",
          "metabolism": "B3 supports NAD/NADP pathways used in energy metabolism and cellular repair."
        },
        "evidenceStrength": "emerging",
        "forWhom": "him",
        "foods": ["Chicken", "Tuna", "Fortified grains"],
        "expectations": {
          "canExpect": [
            "Supports general cellular energy and repair chemistry",
            "Adds B-complex coverage in a formula built around sperm energy and oxidative balance",
            "May be easier to tolerate in niacinamide form"
          ],
          "shouldNotExpect": [
            "A direct semen-analysis change from niacin alone",
            "A reason to add separate high-dose niacin, which can carry liver and glucose risks",
            "Flush-free always meaning risk-free at high doses"
          ]
        }
      },
      "sources": [
        {
          "title": "Niacin Fact Sheet for Health Professionals",
          "journal": "NIH Office of Dietary Supplements",
          "year": 2025,
          "url": "https://ods.od.nih.gov/factsheets/Niacin-HealthProfessional/",
          "evidenceLevel": "review"
        }
      ]
    },
    {
      "id": "supplement-vitamin-b6",
      "kind": "supplement",
      "tags": ["supplements", "his", "optimization", "pre-cycle", "sperm"],
      "title": "Vitamin B6",
      "icon": "💊",
      "body": {
        "name": "Vitamin B6",
        "dosageRange": "AlphaSperm provides 10mg per daily serving",
        "dailyTiming": "Take with the rest of the formula. Avoid adding separate high-dose B6 unless directed.",
        "timing": "Useful as part of a daily preconception stack",
        "bioavailability": {
          "form": "AlphaSperm uses pyridoxyl-5-phosphate, the active P5P form.",
          "absorption": "B6 is water-soluble; excessive long-term dosing is the bigger issue than absorption.",
          "metabolism": "B6 participates in amino-acid metabolism, neurotransmitter synthesis, and homocysteine handling."
        },
        "evidenceStrength": "moderate",
        "forWhom": "him",
        "foods": ["Chicken", "Tuna", "Chickpeas"],
        "expectations": {
          "canExpect": [
            "Supports amino-acid and methylation-adjacent pathways in a male prenatal formula",
            "Works with folate and B12 in one-carbon metabolism",
            "Helps avoid a common B-vitamin gap without megadosing"
          ],
          "shouldNotExpect": [
            "Better fertility from pushing B6 above the safe range",
            "A standalone treatment for sperm count or morphology problems",
            "Safe indefinite high-dose use; chronic excess can cause nerve symptoms"
          ]
        }
      },
      "sources": [
        {
          "title": "Vitamin B6 Fact Sheet for Health Professionals",
          "journal": "NIH Office of Dietary Supplements",
          "year": 2025,
          "url": "https://ods.od.nih.gov/factsheets/VitaminB6-HealthProfessional/",
          "evidenceLevel": "review"
        }
      ]
    },
    {
      "id": "supplement-vitamin-b12",
      "kind": "supplement",
      "tags": ["supplements", "his", "optimization", "pre-cycle", "sperm"],
      "title": "Vitamin B12",
      "icon": "💊",
      "body": {
        "name": "Vitamin B12",
        "dosageRange": "AlphaSperm provides 1,000mcg per daily serving",
        "dailyTiming": "Take with the rest of the formula. People using metformin, PPIs, or vegan diets may need extra B12 attention.",
        "timing": "Useful as part of a daily preconception stack",
        "bioavailability": {
          "form": "AlphaSperm uses methylcobalamin.",
          "absorption": "B12 absorption depends on intrinsic factor and gut health; high oral doses can still absorb partly by passive diffusion.",
          "metabolism": "B12 works with folate in DNA synthesis and methylation, both relevant to rapidly dividing cells."
        },
        "evidenceStrength": "moderate",
        "forWhom": "him",
        "foods": ["Eggs", "Milk", "Fish"],
        "expectations": {
          "canExpect": [
            "Supports DNA synthesis and methylation biology in sperm production",
            "Helps cover low intake in vegan or low-animal-food diets",
            "Pairs with methylfolate in the formula"
          ],
          "shouldNotExpect": [
            "A guaranteed fertility benefit if B12 status is already normal",
            "Correction of pernicious anemia or malabsorption without medical care",
            "A replacement for checking B12 if symptoms or risk factors are present"
          ]
        }
      },
      "sources": [
        {
          "title": "Vitamin B12 Fact Sheet for Health Professionals",
          "journal": "NIH Office of Dietary Supplements",
          "year": 2025,
          "url": "https://ods.od.nih.gov/factsheets/VitaminB12-HealthProfessional/",
          "evidenceLevel": "review"
        }
      ]
    },
    {
      "id": "supplement-biotin-b7",
      "kind": "supplement",
      "tags": ["supplements", "his", "optimization", "pre-cycle", "sperm"],
      "title": "Biotin (B7)",
      "icon": "💊",
      "body": {
        "name": "Biotin / Vitamin B7",
        "dosageRange": "AlphaSperm provides 300mcg per daily serving",
        "dailyTiming": "Take with the rest of the formula. Tell your care team about biotin before thyroid, hormone, or cardiac blood tests.",
        "timing": "Useful as part of a daily preconception stack",
        "bioavailability": {
          "form": "AlphaSperm lists biotin as the active nutrient.",
          "absorption": "Biotin is water-soluble and is usually absorbed well at nutritional doses.",
          "metabolism": "Biotin is a cofactor for enzymes involved in fatty-acid and glucose metabolism."
        },
        "evidenceStrength": "emerging",
        "forWhom": "him",
        "foods": ["Eggs", "Salmon", "Sunflower seeds"],
        "expectations": {
          "canExpect": [
            "Supports basic energy and fatty-acid metabolism",
            "Rounds out the B-vitamin coverage in the formula",
            "May help correct a low-intake gap, though true deficiency is uncommon"
          ],
          "shouldNotExpect": [
            "Direct sperm or pregnancy-outcome improvement from biotin alone",
            "No lab-test impact; biotin can interfere with some immunoassays",
            "A reason to add separate hair/nail biotin on top of the formula"
          ]
        }
      },
      "sources": [
        {
          "title": "Biotin Fact Sheet for Health Professionals",
          "journal": "NIH Office of Dietary Supplements",
          "year": 2025,
          "url": "https://ods.od.nih.gov/factsheets/Biotin-HealthProfessional/",
          "evidenceLevel": "review"
        }
      ]
    },
    {
      "id": "supplement-pantothenic-acid-b5",
      "kind": "supplement",
      "tags": ["supplements", "his", "optimization", "pre-cycle", "sperm"],
      "title": "Pantothenic Acid (B5)",
      "icon": "💊",
      "body": {
        "name": "Pantothenic Acid / Vitamin B5",
        "dosageRange": "AlphaSperm provides 10mg per daily serving",
        "dailyTiming": "Take with the rest of the formula. Food can improve tolerance if B vitamins cause nausea.",
        "timing": "Useful as part of a daily preconception stack",
        "bioavailability": {
          "form": "AlphaSperm uses d-calcium pantothenate.",
          "absorption": "B5 is water-soluble, so steady daily intake is preferred over occasional large dosing.",
          "metabolism": "B5 is part of coenzyme A biology, which is central to energy metabolism."
        },
        "evidenceStrength": "emerging",
        "forWhom": "him",
        "foods": ["Chicken", "Eggs", "Broccoli"],
        "expectations": {
          "canExpect": [
            "Supports cellular energy metabolism through coenzyme A pathways",
            "Complements L-carnitine and other sperm-energy nutrients",
            "Helps make the formula a complete B-vitamin stack"
          ],
          "shouldNotExpect": [
            "A standalone male-fertility effect from B5 alone",
            "Rapid semen changes",
            "A need for separate high-dose B5 unless specifically advised"
          ]
        }
      },
      "sources": [
        {
          "title": "Pantothenic Acid Fact Sheet for Health Professionals",
          "journal": "NIH Office of Dietary Supplements",
          "year": 2025,
          "url": "https://ods.od.nih.gov/factsheets/PantothenicAcid-HealthProfessional/",
          "evidenceLevel": "review"
        }
      ]
    },
    {
      "id": "supplement-copper",
      "kind": "supplement",
      "tags": ["supplements", "his", "optimization", "pre-cycle", "sperm"],
      "title": "Copper",
      "icon": "💊",
      "body": {
        "name": "Copper",
        "dosageRange": "AlphaSperm provides 2mg per daily serving",
        "dailyTiming": "Take with the formula. Avoid adding separate copper unless a clinician recommends it.",
        "timing": "Useful as part of a daily preconception stack",
        "bioavailability": {
          "form": "AlphaSperm uses copper bisglycinate chelate.",
          "absorption": "Chelated minerals are designed for tolerability and absorption, but dose balance still matters.",
          "metabolism": "Copper supports antioxidant enzymes and balances zinc intake; too little or too much can be a problem."
        },
        "evidenceStrength": "emerging",
        "forWhom": "him",
        "foods": ["Oysters", "Organ meats", "Nuts"],
        "expectations": {
          "canExpect": [
            "Balances the formula's zinc content so zinc does not push copper too low",
            "Supports copper-dependent antioxidant enzymes",
            "Helps maintain a mineral stack rather than targeting sperm directly"
          ],
          "shouldNotExpect": [
            "A reason to take extra copper without lab or clinician guidance",
            "Direct improvement in semen parameters from copper alone",
            "Safe high-dose use in people with copper metabolism disorders"
          ]
        }
      },
      "sources": [
        {
          "title": "Copper Fact Sheet for Health Professionals",
          "journal": "NIH Office of Dietary Supplements",
          "year": 2025,
          "url": "https://ods.od.nih.gov/factsheets/Copper-HealthProfessional/",
          "evidenceLevel": "review"
        }
      ]
    },
    {
      "id": "supplement-manganese",
      "kind": "supplement",
      "tags": ["supplements", "his", "optimization", "pre-cycle", "sperm"],
      "title": "Manganese",
      "icon": "💊",
      "body": {
        "name": "Manganese",
        "dosageRange": "AlphaSperm provides 2mg per daily serving",
        "dailyTiming": "Take with the formula. Avoid stacking multiple manganese products.",
        "timing": "Useful as part of a daily preconception stack",
        "bioavailability": {
          "form": "AlphaSperm uses manganese bisglycinate chelate.",
          "absorption": "Manganese absorption is regulated by the body and can be affected by iron status.",
          "metabolism": "Manganese is a cofactor for mitochondrial manganese superoxide dismutase, an antioxidant enzyme."
        },
        "evidenceStrength": "emerging",
        "forWhom": "him",
        "foods": ["Brown rice", "Nuts", "Leafy greens"],
        "expectations": {
          "canExpect": [
            "Supports antioxidant-enzyme function in mitochondria",
            "Contributes to the broad antioxidant/mineral design of AlphaSperm",
            "Helps fill trace-mineral gaps without being a primary fertility treatment"
          ],
          "shouldNotExpect": [
            "A direct proven semen-analysis benefit from manganese alone",
            "Benefit from high-dose stacking; excess manganese can be harmful",
            "A replacement for addressing occupational or environmental exposures"
          ]
        }
      },
      "sources": [
        {
          "title": "Manganese Fact Sheet for Health Professionals",
          "journal": "NIH Office of Dietary Supplements",
          "year": 2025,
          "url": "https://ods.od.nih.gov/factsheets/Manganese-HealthProfessional/",
          "evidenceLevel": "review"
        }
      ]
    },
    {
      "id": "supplement-chromium",
      "kind": "supplement",
      "tags": ["supplements", "his", "optimization", "pre-cycle", "sperm"],
      "title": "Chromium",
      "icon": "💊",
      "body": {
        "name": "Chromium",
        "dosageRange": "AlphaSperm provides 100mcg per daily serving",
        "dailyTiming": "Take with the formula. Discuss first if you use diabetes medication.",
        "timing": "Useful as part of a daily preconception stack",
        "bioavailability": {
          "form": "AlphaSperm uses chromium picolinate.",
          "absorption": "Chromium picolinate is a common supplemental form, but clinical effects vary.",
          "metabolism": "Chromium is mostly discussed in glucose and insulin signaling, which can indirectly matter for reproductive health."
        },
        "evidenceStrength": "emerging",
        "forWhom": "him",
        "foods": ["Broccoli", "Fortified grains", "Meat"],
        "expectations": {
          "canExpect": [
            "Supports metabolic-health framing inside the male prenatal",
            "May be relevant when insulin resistance is part of the bigger picture",
            "Adds trace-mineral coverage at a nutritional dose"
          ],
          "shouldNotExpect": [
            "A sperm-specific effect from chromium alone",
            "Safe unsupervised stacking with glucose-lowering medication or supplements",
            "A substitute for metabolic testing or lifestyle treatment"
          ]
        }
      },
      "sources": [
        {
          "title": "Chromium Fact Sheet for Health Professionals",
          "journal": "NIH Office of Dietary Supplements",
          "year": 2025,
          "url": "https://ods.od.nih.gov/factsheets/Chromium-HealthProfessional/",
          "evidenceLevel": "review"
        }
      ]
    },
    {
      "id": "supplement-molybdenum",
      "kind": "supplement",
      "tags": ["supplements", "his", "optimization", "pre-cycle", "sperm"],
      "title": "Molybdenum",
      "icon": "💊",
      "body": {
        "name": "Molybdenum",
        "dosageRange": "AlphaSperm provides 75mcg per daily serving",
        "dailyTiming": "Take with the formula. Separate supplementation is rarely needed unless directed.",
        "timing": "Useful as part of a daily preconception stack",
        "bioavailability": {
          "form": "AlphaSperm uses molybdenum glycinate chelate.",
          "absorption": "Molybdenum is generally absorbed well from foods and supplements.",
          "metabolism": "It is a cofactor for enzymes involved in sulfur-amino-acid and sulfite metabolism."
        },
        "evidenceStrength": "emerging",
        "forWhom": "him",
        "foods": ["Legumes", "Grains", "Nuts"],
        "expectations": {
          "canExpect": [
            "Supports trace-mineral completeness in the formula",
            "Contributes to sulfur-metabolism enzyme systems",
            "Helps avoid gaps without being a headline fertility ingredient"
          ],
          "shouldNotExpect": [
            "A direct fertility boost from molybdenum alone",
            "Need for separate molybdenum for most people",
            "Benefit from high-dose trace-mineral stacking"
          ]
        }
      },
      "sources": [
        {
          "title": "Molybdenum Fact Sheet for Health Professionals",
          "journal": "NIH Office of Dietary Supplements",
          "year": 2025,
          "url": "https://ods.od.nih.gov/factsheets/Molybdenum-HealthProfessional/",
          "evidenceLevel": "review"
        }
      ]
    },
    {
      "id": "supplement-l-cysteine",
      "kind": "supplement",
      "tags": ["supplements", "his", "optimization", "pre-cycle", "sperm"],
      "title": "L-Cysteine",
      "icon": "💊",
      "body": {
        "name": "L-Cysteine",
        "dosageRange": "AlphaSperm provides 300mg L-cysteine hydrochloride per daily serving",
        "dailyTiming": "Take with the formula. Avoid doubling with high-dose NAC unless your clinician agrees.",
        "timing": "Useful as part of a daily preconception stack",
        "bioavailability": {
          "form": "AlphaSperm uses L-cysteine hydrochloride.",
          "absorption": "Cysteine is an amino acid; supplemental forms contribute to the body's cysteine pool.",
          "metabolism": "Cysteine is a building block for glutathione, one of the body's major antioxidants."
        },
        "evidenceStrength": "emerging",
        "forWhom": "him",
        "foods": ["Chicken", "Eggs", "Legumes"],
        "expectations": {
          "canExpect": [
            "Supports glutathione production and antioxidant capacity",
            "Pairs logically with reduced glutathione and alpha-lipoic acid in the formula",
            "May help oxidative-stress pathways that are relevant to sperm DNA integrity"
          ],
          "shouldNotExpect": [
            "The same evidence base as NAC, even though they are related",
            "Guaranteed improvement in DNA fragmentation or semen parameters",
            "Safe stacking with multiple cysteine/NAC/glutathione products without checking totals"
          ]
        }
      },
      "sources": [
        {
          "title": "N-acetyl-cysteine in male reproductive health",
          "journal": "Antioxidants",
          "year": 2021,
          "url": "https://pmc.ncbi.nlm.nih.gov/articles/PMC8228088/",
          "evidenceLevel": "review"
        }
      ]
    },
    {
      "id": "supplement-glutathione",
      "kind": "supplement",
      "tags": ["supplements", "his", "optimization", "pre-cycle", "sperm"],
      "title": "Glutathione",
      "icon": "💊",
      "body": {
        "name": "L-Glutathione",
        "dosageRange": "AlphaSperm provides 100mg reduced L-glutathione per daily serving",
        "dailyTiming": "Take with the formula. Glutathione products vary widely in form and absorption claims.",
        "timing": "Useful as part of a daily preconception stack",
        "bioavailability": {
          "form": "AlphaSperm uses reduced L-glutathione.",
          "absorption": "Oral glutathione bioavailability is debated; liposomal and reduced forms are marketed to improve exposure.",
          "metabolism": "Glutathione helps neutralize reactive oxygen species and recycle other antioxidants."
        },
        "evidenceStrength": "emerging",
        "forWhom": "him",
        "foods": ["Broccoli", "Spinach", "Avocado"],
        "expectations": {
          "canExpect": [
            "Adds direct antioxidant support to a formula focused on oxidative stress",
            "Works alongside vitamin C, vitamin E, selenium, alpha-lipoic acid, and cysteine",
            "May be relevant when sperm oxidative stress or DNA fragmentation is a concern"
          ],
          "shouldNotExpect": [
            "Clear standalone fertility outcome data from glutathione alone",
            "Identical effects across all glutathione forms",
            "A replacement for lifestyle changes that reduce oxidative stress"
          ]
        }
      },
      "sources": [
        {
          "title": "Glutathione and reproductive health",
          "journal": "Antioxidants",
          "year": 2022,
          "url": "https://pmc.ncbi.nlm.nih.gov/articles/PMC8952544/",
          "evidenceLevel": "review"
        }
      ]
    },
    {
      "id": "supplement-l-methionine",
      "kind": "supplement",
      "tags": ["supplements", "his", "optimization", "pre-cycle", "sperm"],
      "title": "L-Methionine",
      "icon": "💊",
      "body": {
        "name": "L-Methionine",
        "dosageRange": "AlphaSperm provides 25mg per daily serving",
        "dailyTiming": "Take with the formula. Avoid adding separate high-dose methionine unless directed.",
        "timing": "Useful as part of a daily preconception stack",
        "bioavailability": {
          "form": "AlphaSperm uses L-methionine.",
          "absorption": "Methionine is an essential amino acid absorbed from food and supplements.",
          "metabolism": "Methionine feeds one-carbon and methylation pathways connected to DNA methylation biology."
        },
        "evidenceStrength": "emerging",
        "forWhom": "him",
        "foods": ["Eggs", "Fish", "Chicken"],
        "expectations": {
          "canExpect": [
            "Supports methylation chemistry alongside folate, B12, B6, and choline",
            "Fits the formula's sperm epigenetic-health framing",
            "Contributes a small amount rather than a pharmacologic dose"
          ],
          "shouldNotExpect": [
            "A proven standalone sperm epigenetics effect",
            "A reason to add high-dose methionine, which can raise homocysteine concerns",
            "Benefit without adequate folate, B12, and B6 context"
          ]
        }
      },
      "sources": [
        {
          "title": "Methionine metabolism and methylation biology",
          "journal": "Nutrients",
          "year": 2020,
          "url": "https://pmc.ncbi.nlm.nih.gov/articles/PMC7468923/",
          "evidenceLevel": "review"
        }
      ]
    },
    {
      "id": "supplement-lycopene",
      "kind": "supplement",
      "tags": ["supplements", "his", "optimization", "pre-cycle", "sperm"],
      "title": "Lycopene",
      "icon": "💊",
      "body": {
        "name": "Lycopene",
        "dosageRange": "AlphaSperm provides 20mg lycopene extract 5% per daily serving",
        "dailyTiming": "Take with food that contains fat. Tomato-based foods with oil can also provide lycopene.",
        "timing": "Useful as part of a daily preconception stack",
        "bioavailability": {
          "form": "AlphaSperm uses lycopene extract from tomato.",
          "absorption": "Lycopene is fat-soluble and is absorbed better with fat-containing meals.",
          "metabolism": "It is a carotenoid antioxidant found in reproductive tissues and semen."
        },
        "evidenceStrength": "emerging",
        "forWhom": "him",
        "foods": ["Tomatoes", "Watermelon", "Pink grapefruit"],
        "expectations": {
          "canExpect": [
            "Supports antioxidant coverage aimed at sperm oxidative stress",
            "May be relevant to motility, morphology, and DNA-protection pathways",
            "Complements vitamins A/C/E, selenium, CoQ10, and glutathione"
          ],
          "shouldNotExpect": [
            "Guaranteed semen improvement from lycopene alone",
            "The same effect from all tomato or lycopene products",
            "A replacement for medical evaluation of varicocele, infection, or endocrine causes"
          ]
        }
      },
      "sources": [
        {
          "title": "Lycopene and male infertility",
          "journal": "Asian Journal of Andrology",
          "year": 2019,
          "url": "https://pmc.ncbi.nlm.nih.gov/articles/PMC6734605/",
          "evidenceLevel": "review"
        }
      ]
    },
    {
      "id": "supplement-astragalus",
      "kind": "supplement",
      "tags": ["supplements", "his", "optimization", "pre-cycle", "sperm"],
      "title": "Astragalus",
      "icon": "💊",
      "body": {
        "name": "Astragalus extract",
        "dosageRange": "AlphaSperm provides 0.30mg astragalus extract per daily serving",
        "dailyTiming": "Take with the formula. Avoid adding separate immune-active herbs without clinician input.",
        "timing": "Useful as part of a daily preconception stack",
        "bioavailability": {
          "form": "AlphaSperm uses Astragalus membranaceus root extract.",
          "absorption": "Herbal extracts vary by concentration, plant part, and marker compounds.",
          "metabolism": "Astragalus is usually discussed as an adaptogenic or immune-modulating botanical, not a core semen-analysis nutrient."
        },
        "evidenceStrength": "emerging",
        "forWhom": "him",
        "foods": [],
        "expectations": {
          "canExpect": [
            "Adds a small botanical component to a broader antioxidant formula",
            "May support general stress and immune-balance framing",
            "Is best viewed as supportive context, not the main fertility mechanism"
          ],
          "shouldNotExpect": [
            "Direct clinical fertility outcomes from astragalus at this dose",
            "Safe use with immune suppression, autoimmune disease, or transplant medication without clinician review",
            "Standardization across all astragalus products"
          ]
        }
      },
      "sources": [
        {
          "title": "Astragalus",
          "journal": "NCCIH",
          "year": 2025,
          "url": "https://www.nccih.nih.gov/health/astragalus",
          "evidenceLevel": "review"
        }
      ]
    },
    {
      "id": "supplement-alphasperm",
      "kind": "supplement",
      "tags": [
        "supplements",
        "his",
        "optimization",
        "pre-cycle",
        "sperm",
        "evidence-based"
      ],
      "title": "AlphaSperm",
      "icon": "💊",
      "lastReviewed": "2026-06-24",
      "body": {
        "name": "AlphaSperm Men's Prenatal+",
        "aliases": [
          "AlphaSperm",
          "Alpha Sperm",
          "AlphaSperm Prenatal+",
          "AlphaSperm Men's Prenatal",
          "Men's Prenatal+"
        ],
        "dosageRange": "4 capsules daily: 2 capsules in the morning and 2 capsules in the afternoon",
        "dailyTiming": "Take two capsules twice daily, once in the morning and once in the afternoon. Each bottle is a 30-day supply.",
        "timing": "Start at least 3 months before trying to conceive, IVF/IUI, or a planned sperm sample when possible, because a sperm-development cycle takes about 74 days.",
        "bioavailability": {
          "form": "Capsule formula with methylfolate, methylcobalamin, P5P vitamin B6, chelated trace minerals, amino acids, antioxidants, and botanical extracts.",
          "absorption": "The split morning/afternoon schedule helps keep the 4-capsule daily serving tolerable. Fat-soluble nutrients such as vitamins A, D, E, K, CoQ10, lycopene, and resveratrol absorb better with food containing fat.",
          "metabolism": "The formula combines antioxidant defense, mitochondrial energy support, methylation nutrients, trace minerals, and adaptogenic botanicals aimed at the 2-3 month sperm-development window.",
          "cautions": [
            "Check the full stack before combining with another multivitamin, male fertility supplement, zinc, selenium, iodine, vitamin A, vitamin K, ashwagandha, NAC, glutathione, or CoQ10 product.",
            "Ask a clinician first if you take warfarin or anticoagulants, thyroid medication, diabetes medication, immune-suppressing medication, psychiatric medication, or have thyroid, liver, kidney, autoimmune, or cancer history."
          ]
        },
        "evidenceStrength": "emerging",
        "forWhom": "him",
        "expectations": {
          "canExpect": [
            "A broad male prenatal-style stack focused on sperm oxidative stress, mitochondrial energy, methylation, and trace mineral support",
            "A more complete formula than taking a single antioxidant alone, with more than 30 active nutrients on the official label",
            "The most realistic reassessment window is about 3 months, because newly produced sperm need time to appear in the ejaculate",
            "The formula is positioned for male reproductive health and may be useful when oxidative stress, motility, DNA fragmentation, or preconception nutrition are part of the plan"
          ],
          "shouldNotExpect": [
            "A guaranteed pregnancy, live birth, or normalized semen analysis result",
            "An instant effect; sperm development takes roughly 74 days, and clinics often retest after 2-3 months",
            "A replacement for semen analysis, reproductive urology evaluation, varicocele assessment, infection/endocrine workup, or lifestyle changes",
            "A simple add-on if you already take a multivitamin or fertility stack; duplicate zinc, selenium, vitamin A, iodine, B6, and fat-soluble vitamins can matter"
          ]
        },
        "ingredients": [
          {
            "name": "Vitamin A",
            "amount": "1,500mcg RAE",
            "purpose": "Antioxidant and reproductive-tissue support.",
            "linkedBlockId": "supplement-vitamin-a",
            "category": "Vitamins",
            "percentDailyValue": "167%",
            "form": "Beta carotene, retinyl palmitate"
          },
          {
            "name": "Vitamin C",
            "amount": "300mg",
            "purpose": "Water-soluble antioxidant that helps recycle other antioxidants.",
            "linkedBlockId": "supplement-vitamin-c",
            "category": "Vitamins",
            "percentDailyValue": "333%",
            "form": "Calcium ascorbate"
          },
          {
            "name": "Vitamin D",
            "amount": "25mcg",
            "purpose": "Hormone-like vitamin linked with reproductive and immune health.",
            "linkedBlockId": "supplement-vitamin-d",
            "category": "Vitamins",
            "percentDailyValue": "125%",
            "form": "Cholecalciferol"
          },
          {
            "name": "Vitamin E",
            "amount": "15mg alpha-tocopherol",
            "purpose": "Fat-soluble antioxidant commonly discussed in sperm oxidative-stress support.",
            "linkedBlockId": "supplement-vitamin-e",
            "category": "Vitamins",
            "percentDailyValue": "100%",
            "form": "d-alpha tocopherol succinate"
          },
          {
            "name": "Vitamin K",
            "amount": "100mcg",
            "purpose": "Fat-soluble nutrient for normal vitamin K status; important to review with anticoagulants.",
            "linkedBlockId": "supplement-vitamin-k",
            "category": "Vitamins",
            "percentDailyValue": "83%",
            "form": "Phylloquinone"
          },
          {
            "name": "Thiamin (B1)",
            "amount": "4mg",
            "purpose": "B-vitamin support for energy metabolism.",
            "linkedBlockId": "supplement-thiamin-b1",
            "category": "B vitamins",
            "percentDailyValue": "333%",
            "form": "Thiamin hydrochloride"
          },
          {
            "name": "Riboflavin (B2)",
            "amount": "5mg",
            "purpose": "Supports flavin coenzymes involved in energy and antioxidant recycling.",
            "linkedBlockId": "supplement-riboflavin-b2",
            "category": "B vitamins",
            "percentDailyValue": "385%",
            "form": "Riboflavin-5-phosphate"
          },
          {
            "name": "Niacin (B3)",
            "amount": "30mg NE",
            "purpose": "Supports NAD/NADP pathways for energy metabolism and cellular repair.",
            "linkedBlockId": "supplement-niacin-b3",
            "category": "B vitamins",
            "percentDailyValue": "188%",
            "form": "Niacinamide"
          },
          {
            "name": "Vitamin B6",
            "amount": "10mg",
            "purpose": "Active-form B6 for amino-acid and methylation-adjacent pathways.",
            "linkedBlockId": "supplement-vitamin-b6",
            "category": "B vitamins",
            "percentDailyValue": "588%",
            "form": "Pyridoxyl-5-phosphate"
          },
          {
            "name": "Folate",
            "amount": "1,333mcg DFE",
            "purpose": "Methylfolate supports DNA synthesis and methylation biology.",
            "linkedBlockId": "supplement-methylfolate",
            "category": "B vitamins",
            "percentDailyValue": "333%",
            "form": "5-methyltetrahydrofolate"
          },
          {
            "name": "Vitamin B12",
            "amount": "1,000mcg",
            "purpose": "Methylcobalamin supports DNA synthesis, methylation, and nervous-system function.",
            "linkedBlockId": "supplement-vitamin-b12",
            "category": "B vitamins",
            "percentDailyValue": "41,667%",
            "form": "Methylcobalamin"
          },
          {
            "name": "Biotin",
            "amount": "300mcg",
            "purpose": "B-vitamin cofactor for metabolism; also relevant to lab-test interference reminders.",
            "linkedBlockId": "supplement-biotin-b7",
            "category": "B vitamins",
            "percentDailyValue": "1,000%"
          },
          {
            "name": "Pantothenic Acid",
            "amount": "10mg",
            "purpose": "B5 supports coenzyme A and energy metabolism.",
            "linkedBlockId": "supplement-pantothenic-acid-b5",
            "category": "B vitamins",
            "percentDailyValue": "200%",
            "form": "d-calcium pantothenate"
          },
          {
            "name": "Choline",
            "amount": "20mg",
            "purpose": "Methyl donor and membrane nutrient that complements folate/B12 pathways.",
            "linkedBlockId": "supplement-choline",
            "category": "Methylation",
            "percentDailyValue": "4%",
            "form": "Choline bitartrate"
          },
          {
            "name": "Iodine",
            "amount": "150mcg",
            "purpose": "Supports thyroid hormone production, which matters for reproductive health.",
            "linkedBlockId": "supplement-iodine",
            "category": "Minerals",
            "percentDailyValue": "100%",
            "form": "Potassium iodide"
          },
          {
            "name": "Magnesium",
            "amount": "10mg",
            "purpose": "Mineral cofactor for ATP and enzyme systems.",
            "linkedBlockId": "supplement-magnesium",
            "category": "Minerals",
            "percentDailyValue": "2%",
            "form": "Magnesium citrate"
          },
          {
            "name": "Zinc",
            "amount": "10mg",
            "purpose": "Core male fertility mineral involved in spermatogenesis and antioxidant defense.",
            "linkedBlockId": "supplement-zinc",
            "category": "Minerals",
            "percentDailyValue": "91%",
            "form": "Zinc bisglycinate chelate"
          },
          {
            "name": "Selenium",
            "amount": "200mcg",
            "purpose": "Trace mineral for selenoproteins involved in sperm motility and antioxidant defense.",
            "linkedBlockId": "supplement-selenium",
            "category": "Minerals",
            "percentDailyValue": "364%",
            "form": "L-selenomethionine"
          },
          {
            "name": "Copper",
            "amount": "2mg",
            "purpose": "Balances zinc intake and supports antioxidant enzymes.",
            "linkedBlockId": "supplement-copper",
            "category": "Minerals",
            "percentDailyValue": "222%",
            "form": "Copper bisglycinate chelate"
          },
          {
            "name": "Manganese",
            "amount": "2mg",
            "purpose": "Trace-mineral cofactor for mitochondrial antioxidant enzymes.",
            "linkedBlockId": "supplement-manganese",
            "category": "Minerals",
            "percentDailyValue": "87%",
            "form": "Manganese bisglycinate chelate"
          },
          {
            "name": "Chromium",
            "amount": "100mcg",
            "purpose": "Trace mineral tied to glucose/insulin metabolism.",
            "linkedBlockId": "supplement-chromium",
            "category": "Minerals",
            "percentDailyValue": "286%",
            "form": "Chromium picolinate"
          },
          {
            "name": "Molybdenum",
            "amount": "75mcg",
            "purpose": "Trace-mineral cofactor for sulfur-amino-acid metabolism.",
            "linkedBlockId": "supplement-molybdenum",
            "category": "Minerals",
            "percentDailyValue": "167%",
            "form": "Molybdenum glycinate chelate"
          },
          {
            "name": "Ashwagandha extract",
            "amount": "360mg",
            "purpose": "Adaptogenic botanical often marketed for stress and male reproductive support.",
            "linkedBlockId": "supplement-ashwagandha",
            "category": "Botanicals",
            "form": "Withania somnifera root"
          },
          {
            "name": "L-Carnitine L-Tartrate",
            "amount": "310mg",
            "purpose": "Sperm-energy nutrient commonly studied for motility support.",
            "linkedBlockId": "supplement-l-carnitine",
            "category": "Amino acids"
          },
          {
            "name": "Maca powder",
            "amount": "310mg",
            "purpose": "Botanical root marketed for libido and male fertility support; evidence is mixed.",
            "linkedBlockId": "supplement-maca",
            "category": "Botanicals",
            "form": "Lepidium meyenii root"
          },
          {
            "name": "L-Cysteine hydrochloride",
            "amount": "300mg",
            "purpose": "Glutathione building block for antioxidant defense.",
            "linkedBlockId": "supplement-l-cysteine",
            "category": "Amino acids"
          },
          {
            "name": "Alpha Lipoic Acid",
            "amount": "200mg",
            "purpose": "Antioxidant that helps recycle other antioxidants.",
            "linkedBlockId": "supplement-alpha-lipoic-acid",
            "category": "Antioxidants"
          },
          {
            "name": "L-Glutathione",
            "amount": "100mg",
            "purpose": "Reduced glutathione is a central antioxidant in cellular redox balance.",
            "linkedBlockId": "supplement-glutathione",
            "category": "Antioxidants",
            "form": "Reduced"
          },
          {
            "name": "Coenzyme Q10",
            "amount": "60mg",
            "purpose": "Mitochondrial energy and antioxidant nutrient relevant to sperm motility.",
            "linkedBlockId": "supplement-coq10",
            "category": "Antioxidants",
            "form": "Ubiquinone"
          },
          {
            "name": "Resveratrol 50% extract",
            "amount": "48mg",
            "purpose": "Polyphenol antioxidant from Japanese knotweed.",
            "linkedBlockId": "supplement-resveratrol",
            "category": "Antioxidants",
            "form": "Polygonum cuspidatum root"
          },
          {
            "name": "L-Methionine",
            "amount": "25mg",
            "purpose": "Essential amino acid feeding methylation pathways.",
            "linkedBlockId": "supplement-l-methionine",
            "category": "Amino acids"
          },
          {
            "name": "Lycopene extract 5%",
            "amount": "20mg",
            "purpose": "Tomato carotenoid antioxidant found in reproductive tissues.",
            "linkedBlockId": "supplement-lycopene",
            "category": "Antioxidants",
            "form": "Lycopersicum esculentum / tomato"
          },
          {
            "name": "Inositol",
            "amount": "10mg",
            "purpose": "Cell-signaling nutrient included at a small dose in this male prenatal formula.",
            "linkedBlockId": "supplement-myo-inositol",
            "category": "Micronutrients"
          },
          {
            "name": "L-Arginine hydrochloride",
            "amount": "4mg",
            "purpose": "Nitric-oxide precursor involved in vascular and sperm metabolism pathways.",
            "linkedBlockId": "supplement-l-arginine",
            "category": "Amino acids"
          },
          {
            "name": "Astragalus extract",
            "amount": "0.30mg",
            "purpose": "Botanical root extract used as an adaptogenic/immune-support ingredient.",
            "linkedBlockId": "supplement-astragalus",
            "category": "Botanicals",
            "form": "Astragalus membranaceus root"
          }
        ],
        "otherIngredients": [
          "Vegetable cellulose capsule",
          "Rice powder",
          "L-leucine",
          "Magnesium stearate"
        ],
        "purchase": {
          "price": 112.5,
          "originalPrice": 125,
          "retailPrice": 150,
          "currency": "USD",
          "discountLabel": "10% off AlphaSperm",
          "stripeProductId": "supplement-alphasperm",
          "checkoutMode": "stripe"
        },
        "shopLinks": [
          {
            "label": "Order from AlphaSperm",
            "url": "https://alphasperm.com/products/alpha-sperm"
          },
          {
            "label": "View official supplement label",
            "url": "https://alphaspermdemo.myshopify.com/cdn/shop/files/AS-NutritionFacts-1130x1980.png?v=1777296224&width=3840"
          }
        ],
        "interactions": [
          {
            "with": "Warfarin or anticoagulants",
            "severity": "critical",
            "concern": "AlphaSperm contains vitamin K plus botanicals and antioxidants that may complicate anticoagulation management.",
            "recommendation": "Do not start without explicit approval from the clinician managing anticoagulation.",
            "linkedBlockId": "med-warfarin"
          },
          {
            "with": "Levothyroxine or thyroid medication",
            "severity": "caution",
            "concern": "Minerals such as magnesium and zinc can reduce thyroid medication absorption, and the formula contains iodine and ashwagandha.",
            "recommendation": "Separate thyroid medication from supplements as directed and tell your clinician about the full formula.",
            "linkedBlockId": "med-levothyroxine"
          },
          {
            "with": "Other multivitamins or male fertility stacks",
            "severity": "caution",
            "concern": "Duplicate zinc, selenium, iodine, vitamin A, B6, vitamin K, CoQ10, ashwagandha, NAC/cysteine, or glutathione can push totals above intended ranges.",
            "recommendation": "Compare labels before combining products and bring the full list to your care team."
          }
        ]
      },
      "sources": [
        {
          "title": "AlphaSperm product page",
          "journal": "AlphaSperm Inc",
          "year": 2026,
          "url": "https://alphasperm.com/products/alpha-sperm",
          "evidenceLevel": "expert-opinion"
        },
        {
          "title": "AlphaSperm official supplement label",
          "journal": "AlphaSperm Inc",
          "year": 2026,
          "url": "https://alphaspermdemo.myshopify.com/cdn/shop/files/AS-NutritionFacts-1130x1980.png?v=1777296224&width=3840",
          "evidenceLevel": "expert-opinion"
        },
        {
          "title": "AlphaSperm Men's Prenatal+ Quality Certification Program",
          "journal": "ConsumerLab",
          "year": 2025,
          "url": "https://cdn.shopify.com/s/files/1/0039/7321/8393/files/ConsumerLabs_-_AlphaSperm_Report_2026.pdf",
          "evidenceLevel": "review"
        },
        {
          "title": "AlphaSperm-induced epigenetic reprogramming in sperm of infertile men",
          "journal": "Fertility and Sterility",
          "year": 2024,
          "url": "https://www.fertstert.org/article/S0015-0282(24)01901-0/fulltext",
          "evidenceLevel": "observational"
        }
      ]
    }
  ],
  "relationships": [],
  "localeVariants": []
}
